import { useState } from "react";

export function renderErrorSLA() {
  return <ErrorSLAComponent />;
}

function ErrorSLAComponent() {
const [zoomVideo, setZoomVideo] = useState(null);

return (
  <div className=" text-slate-900 dark:text-slate-100 transition-colors duration-300">
  
        {/* Main Content Area */}
        <main className="py-10">
          <div className="mx-auto">
            <div className="flex flex-wrap items-center gap-2 mb-6 text-sm">
              <a className="text-slate-500 dark:text-[#90a9cb] hover:text-primary transition-colors" href="#">Netraa LH</a>
              <span className="text-slate-400 dark:text-slate-600">/</span>
              <a className="text-slate-500 dark:text-[#90a9cb] hover:text-primary transition-colors" href="#">Product Capabilities</a>
              <span className="text-slate-400 dark:text-slate-600">/</span>
              <span className="text-primary font-medium">Errors & SLA</span>
            </div>

            <h1 className="text-5xl lg:text-5xl font-extrabold mb-6 bg-clip-text text-transparent bg-gradient-to-r from-orange-500 to-red-600">Errors &amp; SLA</h1>
            <p className="mb-12 text-xl leading-relaxed text-slate-500 dark:text-slate-400">Monitor and analyze service disruptions through automated error categorization and Service Level Agreement (SLA) performance tracking across all digital touchpoints.</p>

            {/* Error Dashboard Section */}
            <section className="mb-20 scroll-mt-24" id="error-dashboard">
              <div className="mb-8 flex items-center gap-4">
                <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-primary/10 text-primary">
                  <span className="material-symbols-outlined text-3xl">dashboard_customize</span>
                </span>
                <h2 className="text-3xl font-bold text-slate-900 dark:text-white">Error Dashboard</h2>
              </div>

              <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-2 mb-10">
                <div className="rounded-2xl border border-slate-200 bg-white p-5 dark:border-slate-800 dark:bg-slate-800/50">
                  <span className="material-symbols-outlined mb-3 text-red-500 text-3xl">developer_board</span>
                  <h4 className="font-bold">Application</h4>
                  <p className="mt-2 text-sm text-slate-500">Code-level exceptions, logic failures, and runtime crashes.</p>
                </div>

                <div className="rounded-2xl border border-slate-200 bg-white p-5 dark:border-slate-800 dark:bg-slate-800/50">
                  <span className="material-symbols-outlined mb-3 text-blue-500 text-3xl">hub</span>
                  <h4 className="font-bold">Third-Party</h4>
                  <p className="mt-2 text-sm text-slate-500">Failures in external APIs, gateways, or microservices.</p>
                </div>

                <div className="rounded-2xl border border-slate-200 bg-white p-5 dark:border-slate-800 dark:bg-slate-800/50">
                  <span className="material-symbols-outlined mb-3 text-orange-500 text-3xl">palette</span>
                  <h4 className="font-bold">Look &amp; Feel</h4>
                  <p className="mt-2 text-sm text-slate-500">UI rendering issues, broken assets, and layout shifts.</p>
                </div>

                <div className="rounded-2xl border border-slate-200 bg-white p-5 dark:border-slate-800 dark:bg-slate-800/50">
                  <span className="material-symbols-outlined mb-3 text-purple-500 text-3xl">calendar_month</span>
                  <h4 className="font-bold">Downtime</h4>
                  <p className="mt-2 text-sm text-slate-500">Scheduled maintenance windows and system-wide outages.</p>
                </div>
              </div>

                <div className="flex items-center justify-center">
<video
  className="cursor-zoom-in w-25 h-45 object-cover object-top"
  autoPlay
  loop
  muted
  playsInline
  onClick={() => setZoomVideo("/videos/livedashbrd.webm")}
>
  <source src="/videos/livedashbrd.webm" type="video/webm" />
</video>

</div>
            </section>

            {/* Detailed Error Statistics */}
            <section className="mb-20 scroll-mt-24" id="error-statistics">
              <div className="mb-8 flex items-center gap-4">
                <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-primary/10 text-primary">
                  <span className="material-symbols-outlined text-3xl">analytics</span>
                </span>
                <h2 className="text-3xl font-bold text-slate-900 dark:text-white">Detailed Error Statistics</h2>
              </div>

              <div className="prose prose-slate max-w-none dark:prose-invert">
                <p className="text-lg text-slate-500 dark:text-slate-400">Trend analysis is fundamental to proactive maintenance. Netraa LH correlates error timestamps with deployment cycles to pinpoint regressions instantly. The statistical engine provides:</p>
                <ul className="mt-6 space-y-4 text-slate-500 dark:text-slate-400">
                  <li className="flex gap-3"><span className="material-symbols-outlined text-primary">check_circle</span> <strong>Spike Detection:</strong> Automatic alerting when error rates exceed historical standard deviations.</li>
                  <li className="flex gap-3"><span className="material-symbols-outlined text-primary">check_circle</span> <strong>Device Fingerprinting:</strong> Identifying specific hardware or OS versions causing disproportionate failures.</li>
                  <li className="flex gap-3"><span className="material-symbols-outlined text-primary">check_circle</span> <strong>Resolution Impact:</strong> Tracking the Mean Time to Resolution (MTTR) across different technical squads.</li>
                </ul>
              </div>
            </section>

            {/* SLA Management */}
            <section className="mb-20 scroll-mt-24" id="sla-management">
              <div className="mb-8 flex items-center gap-4">
                <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-primary/10 text-primary">
                  <span className="material-symbols-outlined text-3xl">verified_user</span>
                </span>
                <h2 className="text-3xl font-bold text-slate-900 dark:text-white"> SLA Management</h2>
              </div>

              <div className="grid grid-cols-1 gap-8 md:grid-cols-2">
                <div className="rounded-3xl border border-slate-200 bg-white p-8 dark:border-slate-800 dark:bg-slate-800/50">
                  <h3 className="mb-4 text-xl font-bold">Technical vs. Business Availability</h3>
                  <p className="text-slate-500 dark:text-slate-400">Technical availability measures server uptime (ping/heartbeat), whereas Business availability focuses on whether critical user journeys (e.g., checkout) are functional, regardless of backend health.</p>
                </div>
                <div className="rounded-3xl border border-slate-200 bg-white p-8 dark:border-slate-800 dark:bg-slate-800/50">
                  <h3 className="mb-4 text-xl font-bold">User Satisfaction (Apdex)</h3>
                  <p className="text-slate-500 dark:text-slate-400">The Application Performance Index (Apdex) quantifies user experience by categorizing response times into Satisfied, Tolerated, and Frustrated buckets.</p>
                </div>
              </div>

              <div className="mt-8 rounded-3xl bg-slate-100 p-8 dark:bg-slate-900">
                <h4 className="mb-4 font-bold">Performance Percentiles (P90, P95, P99)</h4>
                <p className="text-sm leading-relaxed text-slate-500">While averages are helpful, percentiles provide the true picture of tail latency. A P95 of 2s means 95% of your users experience load times under 2 seconds, while the remaining 5% may face significant lag that requires optimization.</p>
              </div>
            </section>

            {/* Domain & Device Selection */}
            <section className="mb-20 scroll-mt-24" id="device-selection">
              <div className="mb-8 flex items-center gap-4">
                <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-primary/10 text-primary">
                  <span className="material-symbols-outlined text-3xl">devices</span>
                </span>
                <h2 className="text-3xl font-bold text-slate-900 dark:text-white">Domain &amp; Device Selection</h2>
              </div>

              <p className="mb-8 text-slate-500">Switch views between platforms to see platform-specific SLA metrics. The system normalizes data differently for web vs mobile to account for network variability.</p>

              <div className="flex flex-wrap gap-4">
                <button className="flex items-center gap-3 rounded-2xl border-2 border-primary bg-primary/5 px-6 py-4 font-bold text-primary">
                  <span className="material-symbols-outlined">language</span> Web (Desktop)
                </button>
                <button className="flex items-center gap-3 rounded-2xl border-2 border-slate-200 px-6 py-4 font-bold text-slate-400 hover:border-primary/50 hover:text-primary dark:border-slate-800">
                  <span className="material-symbols-outlined">android</span> Android App
                </button>
                <button className="flex items-center gap-3 rounded-2xl border-2 border-slate-200 px-6 py-4 font-bold text-slate-400 hover:border-primary/50 hover:text-primary dark:border-slate-800">
                  <span className="material-symbols-outlined">phone_iphone</span> iOS Native
                </button>
              </div>
            </section>

            {/* SLA KPI Views */}
            <section className="mb-20 scroll-mt-24" id="kpi-views">
              <div className="mb-8 flex items-center gap-4">
                <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-primary/10 text-primary">
                  <span className="material-symbols-outlined text-3xl">query_stats</span>
                </span>
                <h2 className="text-3xl font-bold text-slate-900 dark:text-white">SLA KPI Views</h2>
              </div>

              <div className="grid grid-cols-1 gap-10 lg:grid-cols-2">
                <div>
                  <h3 className="mb-4 text-xl font-bold text-primary">Application KPI</h3>
                  <ul className="space-y-3 text-slate-500">
                    <li className="flex items-start gap-2"><span className="material-symbols-outlined text-primary text-sm mt-1">fiber_manual_record</span> Overall application health score aggregated across all clusters.</li>
                    <li className="flex items-start gap-2"><span className="material-symbols-outlined text-primary text-sm mt-1">fiber_manual_record</span> System-wide error rate (%) relative to total requests.</li>
                    <li className="flex items-start gap-2"><span className="material-symbols-outlined text-primary text-sm mt-1">fiber_manual_record</span> Infrastructure resource utilization (CPU/Memory) impacts.</li>
                  </ul>
                </div>

                <div>
                  <h3 className="mb-4 text-xl font-bold text-primary">Page KPI</h3>
                  <ul className="space-y-3 text-slate-500">
                    <li className="flex items-start gap-2"><span className="material-symbols-outlined text-primary text-sm mt-1">fiber_manual_record</span> First Contentful Paint (FCP) and Time to Interactive (TTI).</li>
                    <li className="flex items-start gap-2"><span className="material-symbols-outlined text-primary text-sm mt-1">fiber_manual_record</span> Route-specific failure rates for critical navigation paths.</li>
                    <li className="flex items-start gap-2"><span className="material-symbols-outlined text-primary text-sm mt-1">fiber_manual_record</span> Client-side script execution delays and blocking time.</li>
                  </ul>
                </div>
              </div>
               <div className="flex items-center justify-center">
<video
  className="cursor-zoom-in w-25 h-45 object-cover object-top"
  autoPlay
  loop
  muted
  playsInline
  onClick={() => setZoomVideo("/videos/livedashbrd.webm")}
>
  <source src="/videos/livedashbrd.webm" type="video/webm" />
</video>

</div>
            </section>
            {zoomVideo && (
  <div
    className="fixed inset-0 bg-black/80 flex items-center justify-center z-50"
    onClick={() => setZoomVideo(null)}
  >
    <video
      className="max-w-[90vw] max-h-[90vh] rounded-lg shadow-2xl"
      autoPlay
      loop
      muted
      playsInline
      controls
    >
      <source src={zoomVideo} type="video/webm" />
    </video>
  </div>
)}
          </div>
        </main>
      </div>
);
}